import { request } from '@/api/client'
import type { VipEntitlement } from '@/api/auth'

export type PurchaseMode = 'auto_renewable' | 'fixed_term' | 'consumable'

export interface BenefitItem {
  icon: string
  title: string
  title_zh?: string
  title_en?: string
  desc: string
  desc_zh?: string
  desc_en?: string
}

export interface VipPackage {
  package_id: string
  name: string
  name_zh?: string
  name_en?: string
  duration_days: number
  price_cny: number
  original_price_cny?: number
  price_usd?: number | null
  original_price_usd?: number | null
  apple_product_id: string
  is_recommended: boolean
  tag?: string
  tag_zh?: string
  tag_en?: string
  accent_color?: string
  gradient?: string
  benefits?: BenefitItem[]
  points_gift?: number
  is_auto_renew: boolean
  purchase_mode: PurchaseMode
}

export interface VipSubscription {
  state_version: string
  package_id: string
  expire_at: string
  provider?: 'apple' | 'alipay' | 'wechat' | null
  is_auto_renew: boolean
  status: 'active' | 'grace' | 'billing_retry' | 'expired'
  entitlement_active: boolean
  expires_at: string
  auto_renew_enabled: boolean
  grace_expires_at?: string | null
  notice: import('@/api/auth').VipNoticeType
  vip_entitlement: VipEntitlement | null
}

export interface SubscriptionManagement {
  provider: 'apple' | 'none'
  supported: boolean
  native_preferred: boolean
  fallback_url?: string | null
  management_url?: string | null
  message: string
}

export const getPackages = () =>
  request<VipPackage[]>('GET', '/app/vip/packages', {
    skipAuth: true,
  })

export const getSubscription = () =>
  request<VipSubscription | null>('GET', '/app/vip/subscription')

export const getSubscriptionManagement = () =>
  request<SubscriptionManagement>('GET', '/app/vip/subscription-management')

export interface CreateOrderResponse {
  order_id: string
  order_no: string
  amount: number
  subject: string
  apple_product_id?: string
  app_account_token?: string
  purchase_mode: PurchaseMode
  checkout_url?: string
  expires_at?: string
}

export type PayPlatform = 'ios' | 'android' | 'web'
export type PayChannel = 'apple_iap' | 'hosted_checkout'

export interface CreateOrderInput {
  packageId: string
  payChannel: PayChannel
  platform: PayPlatform
  storefrontCountry?: string
  offerContextId?: string
  idempotencyKey: string
}

export const createOrder = (input: CreateOrderInput) =>
  request<CreateOrderResponse>('POST', '/app/pay/orders', {
    body: {
      package_id: input.packageId,
      pay_channel: input.payChannel,
      platform: input.platform,
      storefront_country: input.storefrontCountry,
      offer_context_id: input.offerContextId,
    },
    headers: { 'Idempotency-Key': input.idempotencyKey },
  })

export interface VerifyAppleIAPRequest {
  receipt_data: string
  order_id: string
}

export interface VerifyAppleIAPResponse {
  vip_expire_time: string | null
  points_balance_after: number
  transaction_id: string
  membership: VipSubscription | null
  confirmed: true
}

export const verifyAppleIAP = (data: VerifyAppleIAPRequest) =>
  request<VerifyAppleIAPResponse>('POST', '/app/pay/apple-iap/transactions/verify', {
    body: data,
  })

export const recoverAppleIAP = (receiptData: string, orderId?: string | null) =>
  request<VerifyAppleIAPResponse>('POST', '/app/pay/apple-iap/transactions/recover', {
    body: { receipt_data: receiptData, order_id: orderId || undefined },
  })

export interface OrderDetail {
  order_id: string
  package_id: string
  pay_channel: 'apple_iap' | 'hosted_checkout' | 'alipay' | 'wechat'
  status: 'pending' | 'paid' | 'failed'
  amount_cents: number
  created_at: string
  paid_at?: string | null
  fulfillment_status: 'pending' | 'fulfilled' | 'failed'
  checkout_url?: string | null
  failure_code?: string | null
  failure_reason?: string | null
}

export const getOrder = (orderId: string) =>
  request<OrderDetail>('GET', `/app/pay/orders/${orderId}`)

export const cancelAppleOrder = (orderId: string) =>
  request<{
    order_id: string
    status: 'failed'
    failure_code: 'APPLE_USER_CANCELLED'
  }>('POST', `/app/pay/orders/${orderId}/cancel-apple`)
